import type { Texture } from 'pixi.js';
import type { DrawingDTO } from '@shared/protocol/messages';
import type { App } from '../../app/App';
import { isBlank } from '../../drawing/strokes';
import type { FighterArt, Pose } from './FighterView';

/** Scale that makes a texture display at `nominal` px wide whatever its real resolution. */
export function nominalScale(tex: Texture, nominal: number): number {
  return nominal / Math.max(1, tex.width);
}

export interface ArtHandle extends FighterArt {
  release: () => void;
}

/**
 * Resolve the frames for a character: the player's drawing when present,
 * otherwise a built-in atlas doodle. Always call release() when done.
 */
export function acquireArt(app: App, drawing: DrawingDTO | null | undefined, fallbackAtlasId: string): ArtHandle {
  if (drawing && !isBlank(drawing)) {
    const { key, textures } = app.drawings.acquire(drawing);
    let released = false;
    // Player drawings are treated as facing right (the creator says "draw facing right").
    return {
      frames: textures,
      poses: {},
      anchors: { idle: [0.5, 0.96] },
      facing: 1,
      release: () => {
        if (!released) app.drawings.release(key);
        released = true;
      },
    };
  }
  const id = app.assets.has(fallbackAtlasId) ? fallbackAtlasId : 'monster:gum_blob';
  const poses: Partial<Record<Pose, Texture[]>> = {};
  const anchors: FighterArt['anchors'] = { idle: app.assets.anchorOf(id, 'idle') };
  for (const p of ['attack', 'hit', 'ko'] as const) {
    const f = app.assets.getState(id, p);
    if (f !== app.assets.get(id)) {
      poses[p] = f;
      anchors[p] = app.assets.anchorOf(id, p);
    }
  }
  // Catalog art: heroes are drawn facing right, monsters facing left.
  const facing = id.startsWith('monster:') ? -1 : 1;
  return { frames: app.assets.get(id), poses, anchors, facing, release: () => {} };
}
